import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function HomePage({ user, dark, platform, onSelectService }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [showGuide, setShowGuide] = useState(false);

  const c = {
    card: dark ? '#1c1c1c' : '#ffffff',
    cardBorder: dark ? '#2e2e2e' : '#e8e8e8',
    name: dark ? '#eeeeee' : '#111111',
    sub: dark ? '#555555' : '#aaaaaa',
    ver: dark ? '#252525' : '#f5f5f5',
    verBorder: dark ? '#383838' : '#e0e0e0',
    verText: dark ? '#dddddd' : '#333333',
    arrow: dark ? '#555555' : '#bbbbbb',
    bg: dark ? '#0d0d0d' : '#f8f8f8',
  };

  useEffect(() => {
    if (!platform) return;
    setLoading(true);
    axios.get(`${BACKEND}/services?platform=${encodeURIComponent(platform)}`)
      .then(res => { setServices(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [platform]);

  // เรียงตาม group_sort_order ก่อน แล้วตาม sort_order ของ service
  const sorted = [...services].sort((a, b) => {
    const ga = a.group_sort_order ?? 999;
    const gb = b.group_sort_order ?? 999;
    if (ga !== gb) return ga - gb;
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
  });

  // จัดกลุ่มโดยรักษาลำดับตามที่ sort ไว้
  const grouped = [];
  const groupMap = {};
  for (const svc of sorted) {
    const group = svc.group_name || svc.service_group || svc.name;
    if (!groupMap[group]) {
      groupMap[group] = { name: group, services: [] };
      grouped.push(groupMap[group]);
    }
    groupMap[group].services.push(svc);
  }

  const toggleExpand = (group) => {
    setExpanded(prev => ({ ...prev, [group]: !prev[group] }));
  };

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'200px'}}>
      <p style={{color:'#aaa',fontSize:'13px',fontFamily:'Prompt,sans-serif'}}>กำลังโหลดค่ะ...</p>
    </div>
  );

  if (grouped.length === 0) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'200px'}}>
      <p style={{color:'#aaa',fontSize:'13px',fontFamily:'Prompt,sans-serif'}}>ยังไม่มี service ของ {platform} ค่ะ</p>
    </div>
  );

  return (
    <div style={{padding:'12px',display:'flex',flexDirection:'column',gap:'8px',background:c.bg,minHeight:'100vh',fontFamily:'Prompt,sans-serif'}}>

      {/* แบนเนอร์วิธีใช้งาน — พับไว้เป็นค่าเริ่มต้น */}
      <div style={{background:c.card,border:`1px solid ${c.cardBorder}`,borderRadius:'12px',overflow:'hidden'}}>
        <div
          onClick={() => setShowGuide(!showGuide)}
          style={{padding:'10px 12px',display:'flex',alignItems:'center',justifyContent:'space-between',cursor:'pointer'}}
        >
          <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
            <i className="ti ti-book" style={{fontSize:'14px',color:'#FF4500'}} aria-hidden="true"/>
            <span style={{fontSize:'11px',fontWeight:'600',color:c.name}}>วิธีใช้งาน</span>
          </div>
          <i className={`ti ti-chevron-${showGuide ? 'up' : 'down'}`} style={{fontSize:'14px',color:c.arrow}} aria-hidden="true"/>
        </div>

        {showGuide && (
          <div style={{padding:'0 12px 12px',display:'flex',flexDirection:'column',gap:'10px'}}>
            {/* Step icons */}
            <div style={{display:'flex',justifyContent:'center',gap:'6px',alignItems:'center'}}>
              {[
                { icon: 'ti-coin', label: 'เติมเงิน', bg: dark?'#052e16':'#dcfce7', border: '#22c55e', color: '#22c55e' },
                { icon: 'ti-shopping-cart', label: 'ซื้อแพ็ก', bg: dark?'#1a0800':'#fff5f0', border: '#FF4500', color: '#FF4500' },
                { icon: 'ti-bolt', label: 'ใช้เครดิต', bg: dark?'#0d1a2e':'#eff6ff', border: '#3b82f6', color: '#3b82f6' },
              ].map((s, i) => (
                <div key={i} style={{display:'flex',alignItems:'center',gap:'6px'}}>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'3px'}}>
                    <div style={{width:'36px',height:'36px',borderRadius:'50%',background:s.bg,border:`1px solid ${s.border}`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <i className={`ti ${s.icon}`} style={{fontSize:'16px',color:s.color}} aria-hidden="true"/>
                    </div>
                    <span style={{fontSize:'8px',color:c.sub}}>{s.label}</span>
                  </div>
                  {i < 2 && <i className="ti ti-chevron-right" style={{fontSize:'12px',color:dark?'#444':'#ccc',marginTop:'-10px'}} aria-hidden="true"/>}
                </div>
              ))}
            </div>

            {/* Steps */}
            <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:'8px'}}>
                <span style={{fontSize:'10px',color:'#FF4500',fontWeight:'600',flexShrink:0,width:'12px'}}>1.</span>
                <span style={{fontSize:'10px',color:dark?'#ccc':'#444',lineHeight:1.5}}>เติมเงินเข้ากระเป๋า (ปุ่มเติมเครดิต)</span>
              </div>
              <div style={{display:'flex',alignItems:'flex-start',gap:'8px'}}>
                <span style={{fontSize:'10px',color:'#FF4500',fontWeight:'600',flexShrink:0,width:'12px'}}>2.</span>
                <span style={{fontSize:'10px',color:dark?'#ccc':'#444',lineHeight:1.5}}>ซื้อแพ็กบริการที่ต้องการ สามารถแบ่งใช้ได้</span>
              </div>
              <div style={{display:'flex',alignItems:'flex-start',gap:'8px'}}>
                <span style={{fontSize:'10px',color:'#FF4500',fontWeight:'600',flexShrink:0,width:'12px'}}>3.</span>
                <span style={{fontSize:'10px',color:dark?'#ccc':'#444',lineHeight:1.5}}>กดแท็บ "ใช้เครดิต" กรอกลิงก์ สั่งเลย</span>
              </div>
            </div>

            {/* Notes */}
            <div style={{borderTop:`1px solid ${c.cardBorder}`,paddingTop:'8px',display:'flex',flexDirection:'column',gap:'4px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                <i className="ti ti-wallet" style={{fontSize:'12px',color:'#22c55e'}} aria-hidden="true"/>
                <span style={{fontSize:'9px',color:dark?'#86efac':'#15803d'}}>เงินในกระเป๋าไม่มีหมดอายุค่ะ</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                <i className="ti ti-clock" style={{fontSize:'12px',color:'#f59e0b'}} aria-hidden="true"/>
                <span style={{fontSize:'9px',color:dark?'#fbbf24':'#92400e'}}>เครดิตบริการมีอายุ 180 วัน นับจากวันที่ซื้อ</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                <i className="ti ti-alert-triangle" style={{fontSize:'12px',color:'#f87171'}} aria-hidden="true"/>
                <span style={{fontSize:'9px',color:dark?'#fca5a5':'#991b1b'}}>พบปัญหาด้านการใช้งานใดๆ โปรดแจ้งแอดมิน</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* หมวดหมู่บริการ */}
      {grouped.map(({ name: group, services: svcs }) => {
        const isOpen = expanded[group];
        return (
          <div key={group} style={{background:c.card,border:`1px solid ${c.cardBorder}`,borderRadius:'12px',overflow:'hidden'}}>
            <div
              onClick={() => toggleExpand(group)}
              style={{padding:'12px 14px',display:'flex',alignItems:'center',justifyContent:'space-between',cursor:'pointer'}}
            >
              <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                <span style={{fontSize:'13px',fontWeight:'600',color:c.name}}>{group}</span>
                <span style={{fontSize:'10px',color:c.sub}}>{svcs.length} บริการ</span>
              </div>
              <i className={`ti ti-chevron-${isOpen ? 'up' : 'down'}`} style={{fontSize:'16px',color:c.arrow}} aria-hidden="true"/>
            </div>

            {isOpen && (
              <div style={{padding:'0 10px 10px',display:'flex',flexDirection:'column',gap:'6px'}}>
                {svcs.map(svc => (
                  <div
                    key={svc.id}
                    onClick={() => onSelectService(svc)}
                    style={{
                      padding:'10px 12px',
                      background:c.ver,
                      border:`1.5px solid ${c.verBorder}`,
                      borderRadius:'9px',cursor:'pointer',
                      display:'flex',alignItems:'center',justifyContent:'space-between'
                    }}
                  >
                    <span style={{fontSize:'12px',fontWeight:'600',color:c.verText}}>{svc.name}</span>
                    <i className="ti ti-chevron-right" style={{color:c.arrow,fontSize:'16px'}} aria-hidden="true"/>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default HomePage;